import React, { useCallback, useEffect, useRef, useState } from 'react'
import { take } from 'lib/array/arrayUtils'
import useOfferList from 'hooks/Offers/useOfferList'
import { MAX_CAROUSEL_ITEM_COUNT } from '../../../home/components/OfferCarousels/common/constants'
import OfferCarouselLookingForSomethingElse from '../../../home/components/LookingForSomethingElse/OfferCarouselLookingForSomethingElse'
import { LFSEOfferType } from '../../../home/components/LookingForSomethingElse/constants'
import FullWidthCarouselWithAnalytics from 'components/WithAnalytics/Carousel/FullWidthCarouselWithAnalytics'
import CarouselCardLoadingSkeleton from '../../../home/components/CarouselCard/CarouselCardLoadingSkeleton'
import OfferCard from './OfferCards/OfferCard'
import { CAROUSEL_IN_VIEW_PARAMS } from 'home/constants'
import debounce from 'lodash.debounce'
import { EmptyObject } from 'lib/object/objectUtils'

interface Props {
  filters?: App.OfferListFilters;
  carouselType?: LFSEOfferType;
  overrideOfferList?: App.OfferList;
}

function OfferListCarousel(props: Props) {
  const {
    filters = EmptyObject,
    carouselType,
    overrideOfferList,
  } = props

  const [filterOfferList] = useOfferList(filters, { disabled: !!overrideOfferList || !filters })
  const offerList = overrideOfferList ?? filterOfferList
  const [visible, setVisible] = useState<Set<string>>(new Set())
  const [observer, setObserver] = useState<IntersectionObserver>()
  const observed = useRef<Set<string>>(new Set())

  useEffect(() => {
    const seenSet = new Set<string>()
    const delayedSet = debounce(() => {
      if (seenSet.size) {
        setVisible(new Set(seenSet))
      }
    }, 128)
    const observer = new IntersectionObserver((entries) => {
      const visibleElements = entries.filter(entry => entry.isIntersecting).map(entry => entry.target)
      if (visibleElements.length) {
        visibleElements.forEach(el => {
          const id = (el as HTMLDivElement).dataset.id
          if (id) {
            seenSet.add(id)
            observer.unobserve(el)
          }
        })

        delayedSet()
      }
    }, {
      threshold: CAROUSEL_IN_VIEW_PARAMS.threshold,
      rootMargin: CAROUSEL_IN_VIEW_PARAMS.rootMargin,
    })
    setObserver(observer)
  }, [])

  const cardRef = useCallback((node: HTMLDivElement | null) => {
    const id = node?.dataset.id
    if (id && observer && !observed.current.has(id)) {
      observer.observe(node)
      observed.current.add(id)
    }
  }, [observer])

  return <FullWidthCarouselWithAnalytics
    snap="start"
    itemsPerArrow={3}
  >
    {offerList.fetching && <>
      <CarouselCardLoadingSkeleton />
      <CarouselCardLoadingSkeleton />
      <CarouselCardLoadingSkeleton />
      <CarouselCardLoadingSkeleton />
    </> }
    {take(offerList.offerIds, MAX_CAROUSEL_ITEM_COUNT).map((id: string, idx: number) => (
      <OfferCard
        key={id}
        id={id}
        idx={idx}
        visible={visible.has(id)}
        ref={cardRef}
      />
    ))}
    {carouselType && <OfferCarouselLookingForSomethingElse offerType={carouselType} />}
  </FullWidthCarouselWithAnalytics>
}

export default React.memo(OfferListCarousel)
